
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDisciplines, usePillars } from '@/hooks/useDisciplines';
import { Trees, Plus, Edit, Trash2, GripVertical, Folder, FolderOpen } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useSubDisciplines, useCreateSubDiscipline, useUpdateSubDiscipline, useDeleteSubDiscipline } from '@/hooks/useSubDisciplines';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { getSubDisciplinesForDiscipline } from '@/lib/disciplineMapping';

interface Pillar {
  id: string;
  name: string;
  description: string;
  disciplineCount: number;
}

interface SubDiscipline {
  id: string;
  name: string;
  description: string;
  slug: string;
  disciplineId: string;
  nodeCount: number;
}

const EnhancedTaxonomyManager: React.FC = () => {
  const [newPillarName, setNewPillarName] = useState('');
  const [newPillarDescription, setNewPillarDescription] = useState('');
  const [newSubDisciplineName, setNewSubDisciplineName] = useState('');
  const [newSubDisciplineDescription, setNewSubDisciplineDescription] = useState('');
  const [selectedDiscipline, setSelectedDiscipline] = useState<string>('');
  const [expandedPillars, setExpandedPillars] = useState<Set<string>>(new Set());

  const { data: disciplines } = useDisciplines();
  const { data: pillars, refetch: refetchPillars } = usePillars();

  // Sub-discipline hooks
  const { data: allSubDisciplines, refetch: refetchSubDisciplines } = useSubDisciplines();
  const createSubDiscipline = useCreateSubDiscipline();
  const updateSubDiscipline = useUpdateSubDiscipline();
  const deleteSubDiscipline = useDeleteSubDiscipline();

  // Pillar CRUD hooks
  const queryClient = useQueryClient();
  const createPillar = useMutation({
    mutationFn: async (payload: { name: string; description?: string }) => {
      const { data, error } = await supabase.from('pillars').insert([payload]).select('*').single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({ title: 'Pillar created' });
      refetchPillars();
      setNewPillarName('');
      setNewPillarDescription('');
    }
  });

  const updatePillar = useMutation({
    mutationFn: async ({ id, ...patch }: { id: string; name?: string; description?: string }) => {
      const { data, error } = await supabase.from('pillars').update(patch).eq('id', id).select('*').single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({ title: 'Pillar updated' });
      refetchPillars();
    }
  });

  const deletePillar = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { error } = await supabase.from('pillars').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      toast({ title: 'Pillar deleted' });
      refetchPillars();
    }
  });

  // Rewired handlers to use backend
  const handleCreatePillar = () => {
    if (!newPillarName) return;
    createPillar.mutate({
      name: newPillarName,
      description: newPillarDescription
    });
  };

  const handleCreateSubDiscipline = () => {
    if (!newSubDisciplineName || !selectedDiscipline) return;
    // Generate slug from name
    const slug = newSubDisciplineName.trim().toLowerCase().replace(/\s+/g, '-');
    createSubDiscipline.mutate(
      {
        name: newSubDisciplineName,
        description: newSubDisciplineDescription,
        slug,
        discipline_id: selectedDiscipline
      },
      {
        onSuccess: () => {
          toast({ title: 'Sub-discipline created' });
          setNewSubDisciplineName('');
          setNewSubDisciplineDescription('');
          setSelectedDiscipline('');
          refetchSubDisciplines();
        }
      }
    );
  };

  // Utility: get disciplines by pillar id
  const getDisciplinesByPillar = (pillarId: string) => {
    return disciplines?.filter(d => d.pillar_id === pillarId) || [];
  };

  // Utility: get sub-disciplines by discipline id (combine static and database)
  const getSubDisciplinesByDiscipline = (disciplineId: string) => {
    const discipline = disciplines?.find(d => d.id === disciplineId);
    
    // Get static sub-disciplines if available
    const staticSubDisciplines = discipline ? getSubDisciplinesForDiscipline(discipline.slug) : [];
    
    // Get database sub-disciplines
    const dbSubDisciplines = allSubDisciplines?.filter(sub => sub.discipline_id === disciplineId) ?? [];
    
    // Return static ones if available, otherwise database ones
    if (staticSubDisciplines.length > 0) {
      return staticSubDisciplines.map(sub => ({
        id: sub.id,
        name: sub.name,
        description: sub.description,
        slug: sub.slug,
        discipline_id: disciplineId,
        subtopics: (sub as any).subtopics || []
      }));
    }
    
    return dbSubDisciplines;
  };

  // Toggle expand/collapse of a pillar
  const togglePillarExpansion = (pillarId: string) => {
    setExpandedPillars(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pillarId)) {
        newSet.delete(pillarId);
      } else {
        newSet.add(pillarId);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Trees className="h-6 w-6" />
        <h2 className="text-2xl font-bold">Enhanced Taxonomy Management</h2>
      </div>

      <Tabs defaultValue="hierarchy" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="hierarchy">Taxonomy Hierarchy</TabsTrigger>
          <TabsTrigger value="pillars">Manage Pillars</TabsTrigger>
          <TabsTrigger value="subdisciplines">Sub-Disciplines</TabsTrigger>
        </TabsList>

        {/* -------- Hierarchy Tab -------- */}
        <TabsContent value="hierarchy" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Knowledge Taxonomy Structure</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pillars?.map((pillar) => {
                  const isExpanded = expandedPillars.has(pillar.id);
                  const pillarDisciplines = getDisciplinesByPillar(pillar.id);
                  return (
                    <div key={pillar.id} className="border rounded-lg">
                      <div 
                        className="p-4 cursor-pointer hover:bg-muted/50"
                        onClick={() => togglePillarExpansion(pillar.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {isExpanded ? (
                              <FolderOpen className="h-5 w-5 text-black" />
                            ) : (
                              <Folder className="h-5 w-5 text-black" />
                            )}
                            <div>
                              <h3 className="font-semibold text-lg">{pillar.name}</h3>
                              <p className="text-sm text-black">{pillar.description}</p>
                            </div>
                          </div>
                          <Badge variant="secondary">
                            {pillarDisciplines.length} disciplines
                          </Badge>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-4 pb-4 border-t bg-muted/20">
                          <div className="pt-4 space-y-3">
                            {pillarDisciplines.map((discipline) => {
                              const subDisciplines = getSubDisciplinesByDiscipline(discipline.id);
                              return (
                                <div key={discipline.id} className="ml-6">
                                  <div className="flex items-center justify-between p-3 bg-background rounded border">
                                    <div className="flex items-center gap-2">
                                      <Folder className="h-4 w-4 text-green-600" />
                                      <span className="font-medium">{discipline.name}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline" className="text-xs">
                                        {subDisciplines.length} sub-disciplines
                                      </Badge>
                                    </div>
                                  </div>
                                  {subDisciplines.length > 0 && (
                                    <div className="ml-6 mt-2 space-y-2">
                                      {subDisciplines.map((subDiscipline) => (
                                        <div key={subDiscipline.id} className="p-3 text-sm bg-muted/50 rounded-lg border">
                                          <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                              <div className="w-3 h-3 rounded bg-white" />
                                              <span className="font-medium">{subDiscipline.name}</span>
                                            </div>
                                            <Badge variant="outline" className="text-xs">
                                              {(subDiscipline as any).subtopics?.length ? `${(subDiscipline as any).subtopics.length} topics` : 'Static'}
                                            </Badge>
                                          </div>
                                          <p className="text-xs text-black mb-2">{subDiscipline.description}</p>
                                          {(subDiscipline as any).subtopics && (subDiscipline as any).subtopics.length > 0 && (
                                            <div className="mt-2">
                                              <p className="text-xs font-medium text-black mb-1">Key Areas:</p>
                                              <div className="flex flex-wrap gap-1">
                                                {(subDiscipline as any).subtopics.slice(0, 3).map((topic: string, idx: number) => (
                                                  <Badge key={idx} variant="outline" className="text-xs">
                                                    {topic.split(' - ')[0]}
                                                  </Badge>
                                                ))}
                                                {(subDiscipline as any).subtopics.length > 3 && (
                                                  <Badge variant="outline" className="text-xs">
                                                    +{(subDiscipline as any).subtopics.length - 3} more
                                                  </Badge>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* -------- Pillars Tab -------- */}
        <TabsContent value="pillars" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Create New Pillar
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Pillar Name</label>
                  <Input
                    value={newPillarName}
                    onChange={(e) => setNewPillarName(e.target.value)}
                    placeholder="Enter pillar name..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Description</label>
                  <Textarea
                    value={newPillarDescription}
                    onChange={(e) => setNewPillarDescription(e.target.value)}
                    placeholder="Describe this pillar of knowledge..."
                    rows={3}
                  />
                </div>
                <Button
                  onClick={handleCreatePillar}
                  disabled={!newPillarName || createPillar.isPending}
                  className="w-full"
                >
                  {createPillar.isPending ? (
                    <span>Creating...</span>
                  ) : (
                    "Create Pillar"
                  )}
                </Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Existing Pillars</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pillars?.map((pillar) => (
                    <div key={pillar.id} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{pillar.name}</h4>
                          <p className="text-sm text-black">{pillar.description}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updatePillar.mutate(
                              { id: pillar.id, name: prompt('Edit Name', pillar.name) || pillar.name },
                              { onSuccess: () => refetchPillars() }
                            )}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              if(window.confirm("Delete this pillar?")) deletePillar.mutate({ id: pillar.id });
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* -------- SubDisciplines Tab -------- */}
        <TabsContent value="subdisciplines" className="space-y-6">
          <div className="p-4 bg-white border border-blue-200 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">Sub-Discipline Architecture</h3>
            <p className="text-sm text-black">
              This system combines <strong>static sub-disciplines</strong> (predefined with detailed subtopics) 
              and <strong>dynamic sub-disciplines</strong> (created via this interface). Static ones provide rich 
              structure from existing discipline pages, while dynamic ones allow custom organization.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Create Sub-Discipline
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Parent Discipline</label>
                  <Select value={selectedDiscipline} onValueChange={setSelectedDiscipline}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select discipline..." />
                    </SelectTrigger>
                    <SelectContent>
                      {disciplines?.map((discipline) => (
                        <SelectItem key={discipline.id} value={discipline.id}>
                          {discipline.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Sub-Discipline Name</label>
                  <Input
                    value={newSubDisciplineName}
                    onChange={(e) => setNewSubDisciplineName(e.target.value)}
                    placeholder="Enter sub-discipline name..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Description</label>
                  <Textarea
                    value={newSubDisciplineDescription}
                    onChange={(e) => setNewSubDisciplineDescription(e.target.value)}
                    placeholder="Describe this sub-discipline..."
                    rows={3}
                  />
                </div>
                <Button
                  onClick={handleCreateSubDiscipline}
                  disabled={!newSubDisciplineName || !selectedDiscipline || createSubDiscipline.isPending}
                  className="w-full"
                >
                  {createSubDiscipline.isPending ? (
                    <span>Creating...</span>
                  ) : (
                    "Create Sub-Discipline"
                  )}
                </Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Sub-Disciplines by Discipline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {disciplines?.map((discipline) => {
                    const subDisciplines = getSubDisciplinesByDiscipline(discipline.id);
                    return (
                      <div key={discipline.id} className="border rounded-lg p-3">
                        <h4 className="font-medium mb-2">{discipline.name}</h4>
                        {subDisciplines.length === 0 ? (
                          <p className="text-sm text-black">No sub-disciplines</p>
                        ) : (
                          <div className="space-y-2">
                            {subDisciplines.map((subDiscipline) => {
                              const isStatic = !!(subDiscipline as any).subtopics;
                              return (
                                <div key={subDiscipline.id} className="p-3 bg-muted/50 rounded border">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium">{subDiscipline.name}</span>
                                      <Badge variant={isStatic ? "default" : "secondary"} className="text-xs">
                                        {isStatic ? "Static" : "Dynamic"}
                                      </Badge>
                                    </div>
                                    {!isStatic && (
                                      <div className="flex gap-1">
                                        <Button size="sm" variant="ghost" onClick={() => {
                                          const newName = prompt('Edit Name', subDiscipline.name);
                                          if (newName) {
                                            updateSubDiscipline.mutate(
                                              { id: subDiscipline.id, name: newName },
                                              { onSuccess: () => refetchSubDisciplines() }
                                            );
                                          }
                                        }}>
                                          <Edit className="h-3 w-3" />
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => {
                                          if(window.confirm("Delete this sub-discipline?")) deleteSubDiscipline.mutate({ id: subDiscipline.id }, { onSuccess: () => refetchSubDisciplines() });
                                        }}>
                                          <Trash2 className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                  <p className="text-xs text-black mb-2">{subDiscipline.description}</p>
                                  {isStatic && (subDiscipline as any).subtopics && (
                                    <div className="mt-2">
                                      <p className="text-xs font-medium text-black mb-1">Key Areas ({(subDiscipline as any).subtopics.length}):</p>
                                      <div className="flex flex-wrap gap-1">
                                        {(subDiscipline as any).subtopics.slice(0, 4).map((topic: string, idx: number) => (
                                          <Badge key={idx} variant="outline" className="text-xs">
                                            {topic.split(' - ')[0]}
                                          </Badge>
                                        ))}
                                        {(subDiscipline as any).subtopics.length > 4 && (
                                          <Badge variant="outline" className="text-xs">
                                            +{(subDiscipline as any).subtopics.length - 4} more
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EnhancedTaxonomyManager;
